import { Field, Form, withFormik } from "formik";
import { arrayOf, bool, func, shape } from "prop-types";
import React, { useEffect } from "react";
import { Col } from "react-bootstrap";
import { connect } from "react-redux";
import { goBack } from "react-router-redux";
import { compose } from "redux";
import Yup from "yup";

import Button from "pages/_components/Button";
import Container from "pages/_components/Container";
import Credential from "pages/_components/fields/credentials/Credential";
import FormattedAmount from "pages/_components/FormattedAmount";
import FormattedDate from "pages/_components/FormattedDate";
import Head from "pages/_components/Head";
import Heading from "pages/_components/Heading";
import Hint from "pages/_components/hints/Hint";
import I18n from "pages/_components/I18n";
import Notification from "pages/_components/Notification";
import PageLoading from "pages/_components/PageLoading";

import {
    selectors as selectorsTransactionLines,
    actions as transactionLinesActions,
} from "reducers/form/transactionLines";
import { selectors as multilineFileSelectors } from "reducers/formFields/multilineFile";
import {
    actions as multipleTransferActions,
    selectors as multipleTransferSelectors,
} from "reducers/multiple-transfers";
import { selectors as sessionSelectors } from "reducers/session";

import Row from "react-bootstrap/lib/Row";
import { getAccountDescription } from "util/accounts";
import * as configUtils from "util/config";
import * as i18nUtils from "util/i18n";
import isTokenActive from "util/token";
import { getMultipleTransfersI18nKey } from "util/multiple-transfers";
import { getMasspaymentsApiReceiptHint } from "util/multipleTransfersCmf";
import { applyEmbeddedV3ShellLayoutState, clearEmbeddedV3ShellLayoutState } from "util/v3ShellLayoutSync";
import { resizableRoute } from "pages/_components/Resizable";
import MultipleTransferModal from "./MultipleTransferModal";

const FORM_ID = "multiple.transfers";
function MultipleTransferSummary(props) {
    const {
        dispatch,
        fetching,
        isDesktop,
        summary: { account, description, executionDate, fileName, quantity, amount, control },
        processedFileData,
        loggedUser,
        itemsDetail,
        isSubmitting,
        embeddedInV3Shell,
    } = props;

    const handleBack = () => dispatch(goBack());

    useEffect(() => {
        if (!embeddedInV3Shell) {
            return undefined;
        }
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            scopes: ["form"],
            titleKey: getMultipleTransfersI18nKey(`${FORM_ID}.summary.title`),
            goBackPath: "/transfer/multiple/create",
        });
        return () => {
            clearEmbeddedV3ShellLayoutState(dispatch);
        };
    }, [dispatch, embeddedInV3Shell, isDesktop]);

    useEffect(() => {
        const fileId = processedFileData?.idFile;
        if (fileId) {
            dispatch(multipleTransferActions.setRememberFormData(true));

            dispatch(multipleTransferActions.getFileContentsRequest(fileId, false, loggedUser.userId));
            dispatch(transactionLinesActions.initializeEditedLines());
            dispatch(transactionLinesActions.setPageNumber(0));
        } else {
            handleBack();
        }
        // eslint-disable-next-line
    }, [processedFileData]);

    const cmfReceiptHint = getMasspaymentsApiReceiptHint();

    return (
        <PageLoading loading={fetching}>
            {!embeddedInV3Shell && <Notification scopeToShow="form" />}
            <Form>
                {(!embeddedInV3Shell || isDesktop) && (
                    <div className="mb-3 px-5">
                        <Head onBack={handleBack} accessibilityTextId="transfers.main.label" navStyle="default pl-0" />
                        <div className="d-flex">
                            <div>
                                <I18n id={getMultipleTransfersI18nKey(`${FORM_ID}.summary.title`)} component="h1" />
                            </div>
                            {control && (
                                <div className="mx-3">
                                    <Hint
                                        defaultValue={configUtils.get("frontend.mobileSignatureControl.text", "")}
                                        classNameGroup="d-flex align-items-center w-100 mb-2"
                                        classNameHintP="m-0 ms-2"
                                        alwaysShowMessage
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {embeddedInV3Shell && !isDesktop && control && (
                    <div className="mb-3 px-5">
                        <Hint
                            defaultValue={configUtils.get("frontend.mobileSignatureControl.text", "")}
                            classNameGroup="d-flex align-items-center w-100 mb-2"
                            classNameHintP="m-0 ms-2"
                            alwaysShowMessage
                        />
                    </div>
                )}
                <Container className="confirmation__container">
                    <Col md={9} lg={6} className={isDesktop ? "confirmation__form" : "container-white py-2 px-5"}>
                        <I18n
                            id={getMultipleTransfersI18nKey(`${FORM_ID}.summary.confirm`)}
                            component={!isDesktop ? "h2" : "h1"}
                            componentProps={{ className: "text-center" }}
                        />
                        <div className="my-3">
                            <Heading.DataGroup
                                containerClassName="transfer-data data-wrapper-flex"
                                label={`${FORM_ID}.summary.account.label`}
                                labelClassName="no-wrap"
                                data={
                                    <p className="data-desc m-0">
                                        {account?.number !== undefined && getAccountDescription(account)}
                                    </p>
                                }
                                dataClassName="data-desc text-right"
                            />
                            <div className="transfer-data data-wrapper-flex">
                                <span className="data-label no-wrap">
                                    {i18nUtils.get(`${FORM_ID}.summary.reference.label`)}
                                </span>{" "}
                                <span
                                    className="data-desc text-right m-0"
                                    style={{
                                        wordBreak: "break-word",
                                        overflowWrap: "break-word",
                                        maxWidth: "100%",
                                        flex: "1",
                                        textAlign: "right",
                                    }}>
                                    {description}
                                </span>
                            </div>
                            <Heading.DataGroup
                                containerClassName="transfer-data data-wrapper-flex"
                                label={`${FORM_ID}.summary.date.label`}
                                labelClassName="no-wrap"
                                data={<FormattedDate date={executionDate} dateFormat="dd/MM/yyyy" />}
                                dataClassName="data-desc text-right"
                            />
                            <Heading.DataGroup
                                containerClassName="transfer-data data-wrapper-flex"
                                label={`${FORM_ID}.summary.fileName.label`}
                                labelClassName="no-wrap"
                                data={fileName}
                                dataClassName="data-desc text-right"
                            />
                            <Heading.DataGroup
                                containerClassName="transfer-data data-wrapper-flex"
                                label={`${FORM_ID}.summary.quantity.label`}
                                labelClassName="no-wrap"
                                data={quantity}
                                dataClassName="data-desc text-right"
                            />
                            <Heading.DataGroup
                                containerClassName="transfer-data data-wrapper-flex"
                                label={`${FORM_ID}.summary.amount.label`}
                                labelClassName="no-wrap"
                                data={
                                    <FormattedAmount
                                        {...amount}
                                        notBold
                                        className={`${isDesktop ? "f-size-4" : "f-size-65"}`}
                                    />
                                }
                                dataClassName="data-desc text-right"
                            />
                        </div>
                        <Hint
                            classNameMessage=""
                            classNameGroup="justify-content-center d-flex my-3"
                            classNameImage="my-0 ml-2 justify-content-center align-self-center"
                            classNameHintP="aling-items-center my-2"
                            alwaysShowMessage
                            nodeMessage={
                                cmfReceiptHint ? (
                                    <p className="hint-text hint-text-header">{cmfReceiptHint}</p>
                                ) : (
                                    <I18n
                                        id={`${FORM_ID}.summary.hint`}
                                        component="p"
                                        componentProps={{ className: "hint-text hint-text-header" }}
                                        NOTICE_MESSAGE={configUtils.get(
                                            "frontend.MultipleTransfer.notice.message",
                                            null,
                                            {
                                                MAX_ALLOWED_TIME: configUtils.getInteger(
                                                    "frontend.MultipleTransfer.max.allowed.time",
                                                ),
                                            },
                                        )}
                                    />
                                )
                            }
                        />
                        <I18n
                            id={`${FORM_ID}.ticket.list.text`}
                            component="p"
                            componentProps={{ className: "font-ligth" }}
                        />
                        <div className="d-flex justify-content-center">
                            <Button
                                type="button"
                                bsStyle="outline"
                                block={false}
                                label={`${FORM_ID}.showList.button`}
                                className="px-5"
                                onClick={() => dispatch(multipleTransferActions.setShowDetail(true))}
                            />
                        </div>
                        {!isTokenActive(false) && (
                            <>
                                <I18n
                                    component="p"
                                    id="transfer.summary.confirm.label"
                                    componentProps={{
                                        className: "font-light text-center f-size-5",
                                    }}
                                />
                                <Field
                                    placeholderText={i18nUtils.get(`confirmation.otp.placeholder`)}
                                    name="otp"
                                    type="otp"
                                    idForm={FORM_ID}
                                    component={Credential}
                                />
                            </>
                        )}
                        <Container className="flex-grow align-items-center w-100" gridClassName="form-content">
                            <Col md={12} lg={12} className="confirmation__content-button">
                                <Row>
                                    <div className="admin-content-center">
                                        <Button
                                            image="images/arrowRight.svg"
                                            imageStyle="mr-2"
                                            type="submit"
                                            bsStyle="primary"
                                            label="global.confirm"
                                            loading={isSubmitting}
                                        />
                                    </div>
                                </Row>
                            </Col>
                        </Container>
                    </Col>
                </Container>
            </Form>
            <MultipleTransferModal
                isDesktop={isDesktop}
                setShow={(show) => dispatch(multipleTransferActions.setShowDetail(show))}
                list={itemsDetail}
            />
        </PageLoading>
    );
}

MultipleTransferSummary.propTypes = {
    dispatch: func.isRequired,
    isDesktop: bool.isRequired,
    fetching: bool.isRequired,
    processedFileData: shape({}).isRequired,
    summary: shape({}).isRequired,
    itemsDetail: arrayOf().isRequired,
    loggedUser: shape({}).isRequired,
    isSubmitting: bool,
    embeddedInV3Shell: bool,
};

MultipleTransferSummary.defaultProps = {
    isSubmitting: false,
    embeddedInV3Shell: false,
};

const mapStateToProps = (state) => ({
    fetching: multipleTransferSelectors.getFetching(state),
    summary: multipleTransferSelectors.getMultipleTransfer(state),
    itemsDetail: selectorsTransactionLines.getTransactionLines(state),
    processedFileData: multilineFileSelectors.getProcessedFileData(state),
    loggedUser: sessionSelectors.getUser(state),
    showDetailModal: multipleTransferSelectors.showDetailModal(state),
});

export default compose(
    connect(mapStateToProps),
    withFormik({
        mapPropsToValues: () => ({
            otp: "",
        }),
        validationSchema: () =>
            Yup.object().shape({
                otp: Yup.string().required(i18nUtils.get(`${FORM_ID}.emit.field.error.required`)),
            }),
        handleSubmit: ({ otp }, formikBag) => {
            const { props } = formikBag;
            const { dispatch, summary } = props;

            dispatch(multipleTransferActions.createMultipleTransfer(otp, summary, formikBag));
        },
    }),
    resizableRoute,
)(MultipleTransferSummary);
